import { useEffect, useState } from "react";
import { SessionMode } from "../libs/constants/index.js";
import { uploadBlob } from "../services/azure/azure-functions.js";
import { InterviewLogUploader } from "../services/logging/interviewLogUploader.js";
import useAppStore from "../store/AppStore.js";
import { useBoundStore } from "../store/UseBoundStore1.js";
import "../styles/InterviewEndNotification.css";
class ServerSessionEndStatus {
  static INITIAL = "INITIAL";
  static ENDING = "ENDING";
  static ENDED = "ENDED";
}

const InterviewEndNotification = ({
  interviewEnded,
  tenantMngtClient,
  botClient,
  onRedirect,
  blobsToUpload = [],
  setBlobsToUpload,
  storageClient,
}) => {
  // State management
  const [countdown, setCountdown] = useState(3);
  const [isUploadingLogs, setIsUploadingLogs] = useState(true);
  const [isUploadingBlobs, setIsUploadingBlobs] = useState(false);

  // Store data
  const [endServerSessionStatus, setEndServerSessionStatus] = useState(ServerSessionEndStatus.INITIAL);
  const candidateName = useBoundStore((state) => state.username);
  const mode = useBoundStore((state) => state.mode);
  const meetingId = useBoundStore((state) => state.meetingId);
  const sasUrl = useBoundStore((state) => state.sasUrl);
  const canEndServerSessionForLiveAvatar = useAppStore(
    (state) => state.canEndServerSessionForLiveAvatar
  );

  // Handle log upload when interview ends
  useEffect(() => {
    if (interviewEnded && endServerSessionStatus === ServerSessionEndStatus.INITIAL) {
      if (
        meetingId &&
        !isUploadingBlobs &&
        blobsToUpload?.length === 0 &&
        canEndServerSessionForLiveAvatar &&
        mode === SessionMode.WEB_VIDEO
      ) {
        setEndServerSessionStatus(ServerSessionEndStatus.ENDING);

        // Clear storage immediately after sending the request

        tenantMngtClient
          .endSession({
            meetingId,
          })
          .finally(async () => {
            setEndServerSessionStatus(ServerSessionEndStatus.ENDED);
            window.localStorage.clear();
            window.sessionStorage.clear();
          });
          botClient.deleteLiveAvatarContext(meetingId)
      } else if (mode !== SessionMode.WEB_VIDEO) {
        setEndServerSessionStatus(ServerSessionEndStatus.ENDED);
        window.localStorage.clear();
        window.sessionStorage.clear();
      }
    }
  }, [
    canEndServerSessionForLiveAvatar,
    interviewEnded,
    meetingId,
    blobsToUpload,
    isUploadingBlobs,
    endServerSessionStatus
  ]);
  useEffect(() => {
    if (interviewEnded && meetingId && candidateName) {
      setIsUploadingLogs(true);

      console.log("Interview ended, uploading console logs...");
      let interviewLogUploader = new InterviewLogUploader(sasUrl);

      interviewLogUploader
        .uploadInterviewLogsWithRetry(meetingId, candidateName)
        .then((result) => {
          if (result.success) {
            console.log(
              `Console logs uploaded successfully: ${result.filename}`
            );
          } else {
            console.error(`Failed to upload console logs: ${result.error}`);
          }
        })
        .catch((error) => {
          console.error("Error during log upload:", error);
        })
        .finally(() => {
          setIsUploadingLogs(false);
        });
    }
  }, [interviewEnded, meetingId, candidateName]);

  // Handle blob uploads when interview ends
  useEffect(() => {
    if (
      interviewEnded &&
      blobsToUpload?.length > 0 &&
      meetingId &&
      storageClient &&
      !isUploadingBlobs
    ) {
      setIsUploadingBlobs(true);
      console.log(
        "[InterviewEndNotification] Starting upload of pending blobs:",
        {
          blobCount: blobsToUpload.length,
          meetingId,
        }
      );

      // Process all blobs concurrently with Promise.allSettled
      const uploadPromises = blobsToUpload.map((blobWithIndex, i) =>
        uploadBlob(
          blobWithIndex.blob,
          meetingId,
          blobWithIndex.index,
          storageClient
        )
          .then(() => ({
            success: true,
            index: blobWithIndex.index,
            originalIndex: i,
          }))
          .catch((error) => ({
            success: false,
            index: blobWithIndex.index,
            originalIndex: i,
            error,
          }))
      );

      Promise.allSettled(uploadPromises)
        .then((results) => {
          let remainingBlobs = [];
          let successCount = 0;
          let failureCount = 0;

          results.forEach((result, i) => {
            if (result.status === "fulfilled") {
              const uploadResult = result.value;
              if (uploadResult.success) {
                console.log(
                  `[InterviewEndNotification] Staged block chunk ${uploadResult.index} uploaded successfully`
                );
                successCount++;
              } else {
                console.error(
                  `[InterviewEndNotification] Failed to stage block chunk ${uploadResult.index}:`,
                  uploadResult.error
                );
                remainingBlobs.push(blobsToUpload[uploadResult.originalIndex]);
                failureCount++;
              }
            } else {
              console.error(
                `[InterviewEndNotification] Promise rejected for blob ${i}:`,
                result.reason
              );
              remainingBlobs.push(blobsToUpload[i]);
              failureCount++;
            }
          });

          console.log(`[InterviewEndNotification] Blob upload completed:`, {
            successCount,
            failureCount,
            remainingBlobs: remainingBlobs.length,
          });
        })
        .catch((error) => {
          console.error(
            "[InterviewEndNotification] Error during blob upload:",
            error
          );
        })
        .finally(() => {
          setBlobsToUpload([]);
          setIsUploadingBlobs(false);
        });
    }
  }, [
    interviewEnded,
    blobsToUpload,
    meetingId,
    storageClient,
    isUploadingBlobs,
    setBlobsToUpload,
  ]);

  useEffect(() => {
    let timer;

    if (interviewEnded) {
      if (
        blobsToUpload?.length === 0 &&
        !isUploadingLogs &&
        !isUploadingBlobs &&
        endServerSessionStatus === ServerSessionEndStatus.ENDED
      ) {
        // Start countdown when no pending uploads
        timer = setInterval(() => {
          setCountdown((prevCountdown) => {
            if (prevCountdown <= 1) {
              clearInterval(timer);
              onRedirect();
            }
            return prevCountdown - 1;
          });
        }, 900);
      } else if (
        blobsToUpload?.length > 0 ||
        isUploadingLogs ||
        isUploadingBlobs ||
        endServerSessionStatus !== ServerSessionEndStatus.ENDED
      ) {
        // Reset countdown when there are pending uploads, log upload, or blob upload in progress
        setCountdown(3);
      }
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [
    interviewEnded,
    onRedirect,
    blobsToUpload,
    isUploadingLogs,
    isUploadingBlobs,
    endServerSessionStatus,
  ]);

  if (!interviewEnded) return null;

  return (
    <div className="notification">
      {blobsToUpload?.length > 0 ||
      isUploadingLogs ||
      isUploadingBlobs ||
      endServerSessionStatus !== ServerSessionEndStatus.ENDED ? (
        <p>
          Interview is ended. You will be redirected to the next page. Please
          wait.
        </p>
      ) : (
        <p>
          Interview is ended. You will be redirected to the next page in{" "}
          {countdown}.
        </p>
      )}
    </div>
  );
};

export default InterviewEndNotification;
